import { useMutation, useQueryClient } from '@tanstack/react-query'

/** Status envelope the removeUserFromGroup callable resolves with (it does not throw on validation errors). */
export interface LeaveGroupResponse {
  status: 'SUCCESS' | 'ERROR'
  errorCode?: string
  message?: string
}

/**
 * The callable resolves a `{ status }` envelope rather than throwing on failure
 * (an owner attempting to leave gets `OWNER_CANNOT_BE_REMOVED`), so unwrap it
 * into either success or a thrown error the mutation layer can surface.
 */
export function unwrapLeaveGroupResponse(data: LeaveGroupResponse): void {
  if (data.status !== 'SUCCESS') {
    throw new Error(data.message ?? 'Could not leave the team. Please try again.')
  }
}

/**
 * Leaves a team via the `removeUserFromGroup` Firebase callable (europe-west1),
 * passing the current user's own uid — the function treats caller == target as
 * self-removal and allows it without any owner/admin role.
 *
 * Mind the casing: this function IS camelCase, unlike the all-lowercase
 * `removegroup`. There is no single convention among these callables, and a
 * wrong guess names no deployed function, so the call 404s and surfaces as a
 * generic error toast — which reads as "the backend was never built" rather
 * than "the name is wrong". Team delete lost time to exactly that on both
 * platforms, so the name here is copied from the deployed export, not inferred.
 *
 * The function also drops the leaver's dogs from the team and removes them from
 * `userIds`, so on success the my-groups list is invalidated and the team
 * disappears from the user's own list.
 *
 * Mirrors {@link useDeleteGroup}: the Functions SDK is imported lazily to keep
 * it off the synchronous bundle path.
 */
export function useLeaveGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: string; userId: string }): Promise<void> => {
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<{ groupId: string; userId: string }, LeaveGroupResponse>(
        fns,
        'removeUserFromGroup',
      )
      const res = await call({ groupId, userId })
      unwrapLeaveGroupResponse(res.data)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'groups'] })
    },
  })
}
